<template>
    <Teleport to="body">
        <div class="au-drawer-overlay" :class="{ 'is-open': visible }" @click="$emit('close')"></div>
        <aside class="au-drawer" :class="{ 'is-open': visible }" role="dialog" aria-modal="true"
            :aria-label="client?.name ?? ''" :aria-hidden="!visible">
            <template v-if="client">
                <div class="au-drawer-head">
                    <div class="d-flex align-items-start gap-3">
                        <div class="au-avatar"><i class="bi bi-buildings"></i></div>
                        <div class="flex-grow-1" style="min-width: 0;">
                            <h2 class="au-drawer-name">{{ client.name }}</h2>
                            <div class="mt-2">
                                <span class="au-chip"><i class="bi bi-shop"></i>{{ client.distributorName }}</span>
                            </div>
                            <div class="au-drawer-desc">{{ client.description || $t("clients.noDescription") }}</div>
                        </div>
                        <button type="button" class="au-drawer-icon" :title="$t('clients.editClient')"
                            :aria-label="$t('clients.editClient')" @click="$emit('edit', client.id)">
                            <i class="bi bi-pencil"></i>
                        </button>
                        <button type="button" class="au-drawer-icon" :title="$t('clients.closePanel')"
                            :aria-label="$t('clients.closePanel')" @click="$emit('close')">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div class="au-drawer-body">
                    <div class="au-drawer-section">
                        <h3><i class="bi bi-patch-check-fill text-success"></i>{{ $t("licenses.panel.title") }}</h3>
                        <bs5-base-button variant="primary" size="sm" @click="$emit('issue', client.id)">
                            <i class="bi bi-file-earmark-plus"></i>{{ $t("clients.generateLicense") }}
                        </bs5-base-button>
                    </div>

                    <div v-if="loading" class="au-empty">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">{{ $t("common.loading") }}</span>
                        </div>
                    </div>
                    <div v-else-if="activeLicenses.length === 0" class="au-drawer-empty">
                        <i class="bi bi-patch-question fs-3 d-block mb-2"></i>
                        <div>{{ $t("licenses.panel.empty") }}</div>
                        <div>{{ $t("licenses.panel.emptyHint") }}</div>
                    </div>
                    <template v-else>
                        <div v-for="license in activeLicenses" :key="license.id" class="au-lic-card"
                            :class="{ 'is-new': newLicenseIds.includes(license.id as string) }">
                            <div class="d-flex align-items-center gap-2">
                                <span class="badge" :class="badgeClass(license)">{{ stateLabel(license) }}</span>
                                <span v-if="newLicenseIds.includes(license.id as string)" class="badge bg-primary">
                                    {{ $t("licenses.panel.new") }}
                                </span>
                                <span class="ms-auto fw-semibold">{{ validity(license) }}</span>
                            </div>
                            <div class="mt-2">
                                <span class="au-lic-key">
                                    <i class="bi bi-cpu"></i>{{ license.licenseKey }}
                                    <button type="button" :title="$t('licenses.panel.copyKey')"
                                        :aria-label="$t('licenses.panel.copyKey')" @click="copyKey(license.licenseKey)">
                                        <i class="bi bi-copy"></i>
                                    </button>
                                </span>
                            </div>
                            <div class="d-flex align-items-center mt-2 gap-2">
                                <span class="au-lic-meta">
                                    <i class="bi bi-calendar-check me-1"></i>
                                    {{ $t("licenses.panel.issuedOn", { date: formatDate(license.issuedDate) }) }}
                                </span>
                                <bs5-base-button variant="primary" size="sm" class="ms-auto"
                                    @click="downloadLicense(license)">
                                    <i class="bi bi-download"></i>{{ $t("licenses.download") }}
                                </bs5-base-button>
                                <bs5-action-menu :label="$t('licenses.panel.more')">
                                    <button type="button" class="au-menu-item is-danger" role="menuitem"
                                        @click="deleteLicense(license)">
                                        <i class="bi bi-slash-circle"></i>{{ $t("licenses.panel.removeFromPanel") }}
                                    </button>
                                </bs5-action-menu>
                            </div>
                        </div>
                    </template>

                    <template v-if="!loading && inactiveLicenses.length > 0">
                        <button type="button" class="au-link-btn" :aria-expanded="showInactive"
                            @click="showInactive = !showInactive">
                            <i :class="['bi', showInactive ? 'bi-chevron-up' : 'bi-chevron-down', 'me-1']"></i>
                            {{ $t(showInactive ? "licenses.panel.hideInactive" : "licenses.panel.showInactive",
                                { count: inactiveLicenses.length }) }}
                        </button>
                        <div v-if="showInactive">
                            <div v-for="license in inactiveLicenses" :key="license.id" class="au-lic-card is-off">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge bg-secondary">{{ $t("licenses.inactive") }}</span>
                                    <span class="ms-auto fw-semibold">{{ validity(license) }}</span>
                                </div>
                                <div class="mt-2"><span class="au-lic-key"><i class="bi bi-cpu"></i>{{ license.licenseKey }}</span></div>
                                <div class="au-lic-meta mt-2">
                                    <i class="bi bi-calendar-check me-1"></i>
                                    {{ $t("licenses.panel.issuedOn", { date: formatDate(license.issuedDate) }) }}
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </aside>
    </Teleport>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { t } from "@/helpers/i18n";
import licenseService from "@/services/license.service";
import { License } from "@/models/license.model";
import { Client } from "@/models/client.model";
import { showToast } from "@/utils/toast";
import { formatDate } from "@/utils/date";
import { daysUntil, expiringText, licenseState } from "@/utils/license-status";

type PanelClient = Client & { distributorName: string };

export default defineComponent({
    name: "ClientPanel",
    props: {
        visible: { type: Boolean, required: true },
        client: { type: Object as PropType<PanelClient | null>, default: null },
        licenses: { type: Array as PropType<License[]>, default: () => [] },
        loading: { type: Boolean, default: false },
        newLicenseIds: { type: Array as PropType<string[]>, default: () => [] },
    },
    emits: ["close", "edit", "issue", "changed"],
    data() {
        return {
            showInactive: false,
        };
    },
    computed: {
        activeLicenses(): License[] {
            return this.licenses.filter(license => license.isActive);
        },
        inactiveLicenses(): License[] {
            return this.licenses.filter(license => !license.isActive);
        },
    },
    watch: {
        client() {
            this.showInactive = false;
        },
        visible(isVisible: boolean) {
            if (isVisible) {
                document.addEventListener("keydown", this.onKeydown);
            } else {
                document.removeEventListener("keydown", this.onKeydown);
            }
        },
    },
    methods: {
        formatDate,
        stateLabel(license: License): string {
            const state = licenseState(license);
            if (state === "expired") {
                return t("licenses.summary.expired");
            }
            if (state === "expiring") {
                return expiringText(daysUntil(license.expireDate as string));
            }
            return state === "inactive" ? t("licenses.inactive") : t("licenses.active");
        },
        badgeClass(license: License): string {
            const classes: Record<string, string> = {
                active: "bg-success",
                expiring: "bg-warning",
                expired: "bg-danger",
                inactive: "bg-secondary",
            };
            return classes[licenseState(license)];
        },
        validity(license: License): string {
            return license.expireDate
                ? t("licenses.panel.validTo", { date: formatDate(license.expireDate) })
                : t("licenses.panel.perpetual");
        },
        async copyKey(key: string) {
            try {
                await navigator.clipboard.writeText(key);
                showToast(t("licenses.panel.keyCopied"), "success");
            } catch (error) {
                showToast(t("licenses.panel.copyFailed"), "error");
            }
        },
        async downloadLicense(license: License) {
            try {
                await licenseService.downloadLicense(this.client?.id as string, license.id as string);
                showToast(t("licenses.downloaded"), "success");
            } catch (error) {
                showToast(t("licenses.downloadFailed"), "error");
            }
        },
        async deleteLicense(license: License) {
            const response = await this.$swal.fire({
                title: t("licenses.deleteTitle"),
                text: t("licenses.deleteText"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("common.confirmDeleteButton"),
                cancelButtonText: t("common.cancel"),
            });
            if (!response.isConfirmed) {
                return;
            }
            try {
                await licenseService.deleteLicense(this.client?.id as string, license.id as string);
                showToast(t("licenses.deleted"), "success");
                this.$emit("changed");
            } catch (error) {
                showToast(t("licenses.deleteFailed"), "error");
            }
        },
        onKeydown(event: KeyboardEvent) {
            if (event.key === "Escape") {
                this.$emit("close");
            }
        },
    },
    beforeUnmount() {
        document.removeEventListener("keydown", this.onKeydown);
    },
});
</script>
